import React, { useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "src/components/Table";
import { NoTicketsMessage } from "src/components/ListScreen";
import { useIsMobile } from "src/hooks/useIsMobile";
import { getIsTicketsLoading, getTickets } from "src/redux/selectors/workRequest";
import { Ticket } from "src/types";
import { vendorTableHeaders } from "../constant";
import VendorListItem from "./VendorListItem";
import VendorTableRow from "./VendorTableRow";
import { getAllActiveResources } from "src/redux/selectors/resources";
import { getUserData } from "src/redux/selectors/user";
import { getResourseData } from "src/components/molecules/WorkRequestListViewContainer/handler";
import WorkRequestListContext from "src/pages/WorkRequests/context";
import styled from "styled-components";
import { colors } from "src/styles";
import ExportWorkOrdersModal from "src/components/organisms/ExportWorkOrdersModal";
import { ExportWorkOrdersFormValues } from "src/components/organisms/ExportWorkOrdersModal/types";
import { FirebaseDataService } from "src/services/Firebase/data";

const VendorTicketsWrapper = () => {
  const isTicketsLoading = useSelector(getIsTicketsLoading);
  const tickets = useSelector(getTickets);
  const { id: loggedInUserId, isManager, vendorId } = useSelector(getUserData);
  const { employees, vendors } = useSelector(getAllActiveResources);
  const { activeTab, searchText } = useContext(WorkRequestListContext);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportingWorkOrders, setIsExportingWorkOrders] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    wonum: "",
    dateCreated: "",
    client: "",
    location: "",
    description: "",
    priority: "",
    status: "",
    assignee: "",
  });

  const { isMobile } = useIsMobile();
  const filteredTickets = useMemo(
    () =>
      tickets.filter((ticket: Ticket) => {
        const assignee = getResourseData(ticket.vendorId, ticket.laborId, vendors, employees) ?? "Unassigned";
        const searchFields = {
          wonum: ticket.displayWonum || ticket.wonum,
          dateCreated: ticket.dateCreated,
          client: ticket.client,
          location: ticket.location,
          description: ticket.description ?? "",
          priority: ticket.priority ?? "",
          status: ticket.statusDescription ?? ticket.status ?? "",
          assignee,
        };

        return Object.entries(columnFilters).every(([key, value]) => {
          const query = value.trim().toLowerCase();
          if (!query) return true;
          return String(searchFields[key as keyof typeof searchFields] ?? "")
            .toLowerCase()
            .includes(query);
        });
      }),
    [tickets, columnFilters, employees, vendors],
  );

  const filterFields = [
    { key: "wonum", label: "Work Order Number" },
    { key: "dateCreated", label: "Date" },
    { key: "client", label: "Customer" },
    { key: "location", label: "Location" },
    { key: "description", label: "Work Request Description" },
    { key: "priority", label: "Priority Level" },
    { key: "status", label: "Status" },
    { key: "assignee", label: "Assignee" },
  ];

  const onClearFilters = () => {
    setColumnFilters({
      wonum: "",
      dateCreated: "",
      client: "",
      location: "",
      description: "",
      priority: "",
      status: "",
      assignee: "",
    });
  };

  const onChangeFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onExport = () => {
    setIsExportModalOpen(true);
  };

  const onSubmitExport = async ({
    fields,
    dateRangeOption,
    fromDate,
    toDate,
  }: ExportWorkOrdersFormValues): Promise<boolean> => {
    if (!loggedInUserId) {
      alert("Unable to export work orders. User session is missing.");
      return false;
    }

    setIsExportingWorkOrders(true);

    try {
      const response = await FirebaseDataService.exportWorkOrders({
        loggedInUserId,
        isManager,
        vendorId: vendorId || undefined,
        searchTerm: searchText?.trim() || undefined,
        workRequestType: activeTab || undefined,
        fields,
        dateRangeOption,
        fromDate,
        toDate,
      });

      const { fileData, fileName, mimeType } = response.data;
      const byteCharacters = atob(fileData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);

      alert("File exported successfully");
      return true;
    } catch (error) {
      console.error("Error exporting work orders:", error);
      alert("An error occurred while exporting work orders. Please try again.");
      return false;
    } finally {
      setIsExportingWorkOrders(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {filteredTickets.map((ticket: Ticket) => (
          <VendorListItem ticket={ticket} key={ticket.wonum} />
        ))}
        {!filteredTickets.length && (
          <NoTicketsMessage $isMobile={isMobile}>No work requests match these filters</NoTicketsMessage>
        )}
      </>
    );
  }
  return (
    <>
      <LegacyWorkOrdersTable
        isLoading={isTicketsLoading}
        headers={vendorTableHeaders}
        filterFields={filterFields}
        filterValues={columnFilters}
        showFilters={showColumnFilters}
        onToggleFilters={() => setShowColumnFilters((prev) => !prev)}
        onFilterChange={onChangeFilter}
        onClearFilters={onClearFilters}
        onExport={onExport}
        exportDisabled={!filteredTickets.length}
      >
        {filteredTickets.map((ticket: Ticket) => (
          <VendorTableRow key={ticket.wonum} ticket={ticket} />
        ))}
      </LegacyWorkOrdersTable>
      {!filteredTickets.length && (
        <NoTicketsMessage $isMobile={isMobile}>No work requests match these filters</NoTicketsMessage>
      )}
      <ExportWorkOrdersModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={onSubmitExport}
        isSubmitting={isExportingWorkOrders}
      />
    </>
  );
};

export default VendorTicketsWrapper;

const LegacyWorkOrdersTable = styled(DataTable)`
  table {
    background: ${colors.white.default};
  }

  th {
    padding: 12px 15px;
    color: ${colors.white.default};
    background: ${colors.black.dark1};
    border-bottom: 1px solid ${colors.grey.light15};
    text-align: left;
  }

  tbody > tr:nth-child(2) th {
    background: ${colors.grey.light1};
    color: ${colors.black.light2};
    border-bottom: 1px solid ${colors.grey.light12};
    padding: 8px 10px;
  }

  tbody > tr:nth-child(2) input {
    border: 1px solid ${colors.grey.light12};
    padding: 6px 8px;
  }

  tbody > tr:nth-child(2) input:focus {
    border-color: ${colors.grey.dark};
  }
`;
